import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Stack, Iconify } from '@pflegenavi/web-components';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';
import {
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
} from '@mui/lab';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { getPendingChargeViewData } from '@pflegenavi/shared/frontend';
import { PaymentInitiatedBy } from '@pflegenavi/shared/api';
import type { PendingCharge } from '@pflegenavi/frontend/api-nursing-home';

type SimplifiedPendingCharge = Pick<
  PendingCharge,
  | 'charge_date'
  | 'payment_initiation'
  | 'amount'
  | 'pending_charge_status'
  | 'family_member_first_name'
  | 'family_member_last_name'
>;

interface FMPendingChargeItemProps {
  charge: SimplifiedPendingCharge;
  currentBalance: number;
  newBalance: number;
  nursingHomeName: string;
  isLast: boolean;
  showInTimeline?: boolean;
}

export const FMPendingChargeItem: FC<FMPendingChargeItemProps> = ({
  charge,
  currentBalance,
  newBalance,
  nursingHomeName,
  isLast,
  showInTimeline = false,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();

  const chargeDate = charge.charge_date;

  // Get formatted data from shared formatter
  const viewData = useMemo(() => {
    return getPendingChargeViewData(charge, nursingHomeName, t);
  }, [charge, nursingHomeName, t]);

  // Keep icon logic separate since it's not in the formatter
  const icon = useMemo(() => {
    const iconName =
      charge.payment_initiation === PaymentInitiatedBy.FamilyMember
        ? 'eva:people-fill'
        : charge.payment_initiation === PaymentInitiatedBy.Employee
        ? 'eva:person-fill'
        : 'eva:settings-2-fill';

    const tooltipTitle =
      charge.payment_initiation === PaymentInitiatedBy.FamilyMember
        ? t('web-components.initiator.family-member')
        : charge.payment_initiation === PaymentInitiatedBy.Employee
        ? t('web-components.initiator.employee')
        : t('web-components.initiator.system');

    return (
      <Tooltip title={tooltipTitle} arrow>
        <Iconify sx={{ mr: 1 }} icon={iconName} fontSize={20} />
      </Tooltip>
    );
  }, [charge.payment_initiation, t]);

  const content = (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon}
        <Typography variant="h6" fontWeight={600}>
          {viewData.typeText}
        </Typography>
        {viewData.statusText && (
          <Typography variant="body2" color="text.secondary">
            ({viewData.statusText})
          </Typography>
        )}
      </Stack>

      <Stack direction="row" spacing={4}>
        <Stack>
          <Typography variant="caption" color="text.secondary">
            {t('mobile.family-member.payment-initiation-date')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {new Date(chargeDate).toLocaleDateString(
              'de-DE',
              YEAR_MONTH_DAY_SHORT_FORMAT
            )}
          </Typography>
        </Stack>
        <Stack>
          <Typography variant="caption" color="text.secondary">
            {t('mobile.family-member.payment-amount')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {fCurrencyCents(charge.amount)}
          </Typography>
        </Stack>
        {!showInTimeline && (
          <Stack>
            <Typography variant="caption" color="text.secondary">
              {t('mobile.family-member.new-account-balance')}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {fCurrencyCents(newBalance)}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );

  if (showInTimeline) {
    return (
      <TimelineItem>
        <TimelineSeparator>
          <TimelineDot
            color={viewData.color as 'error' | 'warning' | 'info' | 'success'}
          />
          {!isLast && <TimelineConnector />}
        </TimelineSeparator>
        <TimelineContent sx={{ paddingTop: 0.25, paddingBottom: 2 }}>
          {content}
        </TimelineContent>
      </TimelineItem>
    );
  }

  return <Box sx={{ pt: 1, pb: 2, px: 2 }}>{content}</Box>;
};
